import { Link, useNavigate } from "react-router-dom";
import { Check, Star, Sparkles, Crown, Rocket, Zap, ShieldCheck, BadgeCheck, Award, TrendingUp, Scale, Building2, User, Megaphone, Target, Video, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { startSubscriptionCheckout } from "../lib/razorpay";
import { useEffect, useState } from "react";
import Seo from "../components/Seo";
import { api } from "../lib/api";
import BoostPropertyPicker from "../components/BoostPropertyPicker";

const PLANS = [
    {
        id: "starter",
        name: "Starter",
        tier: "Entry",
        icon: Sparkles,
        price: "₹1,000",
        priceSuffix: "/ 1 month",
        description: "For first-time partners — perfect to test the platform before committing long term.",
        audience: ["Buyers", "Brokers", "Advocates"],
        features: [
            "30-day unlimited platform access",
            "10 active property listings (Brokers)",
            "Verified profile badge",
            "WhatsApp inquiry support",
            "Local area visibility",
            "Save unlimited properties (Buyers)",
            "Direct contact with sellers & owners",
            "Renews after 30 days",
        ],
        cta: "Get Started",
        accent: false,
    },
    {
        id: "silver",
        name: "Silver",
        tier: "3-Month Pack",
        icon: Rocket,
        price: "₹2,500",
        priceSuffix: "/ 3 months",
        description: "Best for active brokers & advocates working through a complete quarterly cycle.",
        audience: ["Buyers", "Brokers", "Advocates"],
        features: [
            "Everything in Starter",
            "90 days continuous access",
            "20 active property listings (Brokers)",
            "Priority WhatsApp & email support",
            "Quarterly performance report",
            "Saves 17% vs monthly billing",
            "Featured-area campaign opt-in",
            "Basic legal-consultation requests (Advocates)",
        ],
        cta: "Choose Silver",
        accent: false,
    },
    {
        id: "gold",
        name: "Gold",
        tier: "Most Popular",
        icon: Zap,
        price: "₹4,500",
        priceSuffix: "/ 6 months",
        description: "For growing real-estate professionals, advocates & power buyers — best value.",
        audience: ["Buyers", "Brokers", "Advocates"],
        features: [
            "Everything in Silver",
            "180 days continuous access",
            "50 active property listings (Brokers)",
            "Featured property boost",
            "Priority search ranking",
            "AI-generated property descriptions",
            "Premium advocate profile page",
            "Saves 25% vs monthly billing",
        ],
        cta: "Go Gold",
        accent: true,
        tag: "MOST POPULAR",
    },
    {
        id: "platinum",
        name: "Platinum",
        tier: "Annual Elite",
        icon: Crown,
        price: "₹6,500",
        priceSuffix: "/ 1 year",
        description: "For top brokers, developers, leading advocates & VIP investors — best price per month.",
        audience: ["Investors", "Developers", "Top Brokers", "Top Advocates"],
        features: [
            "Everything in Gold",
            "365 days unlimited access",
            "Unlimited property listings",
            "Homepage promotion slots",
            "Dedicated account manager",
            "Reel & video property promotion",
            "Featured-advocate spotlight (Advocates)",
            "Saves 60% vs monthly billing",
            "24×7 priority phone support",
        ],
        cta: "Become Platinum",
        accent: false,
    },
];

const TRUST_BADGES = [
    { icon: ShieldCheck, label: "Secure Payments" },
    { icon: BadgeCheck, label: "RERA-Verified Network" },
    { icon: Award, label: "Trusted by 1,200+ Partners" },
    { icon: TrendingUp, label: "12K+ Active Listings" },
];

const AUDIENCE_ICON = {
    Buyers: User,
    Brokers: Building2,
    Advocates: Scale,
    Investors: TrendingUp,
    Developers: Building2,
    "Top Brokers": Building2,
    "Top Advocates": Scale,
};

export default function Pricing() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [paying, setPaying] = useState(null); // plan id currently being paid
    const [bonusBalance, setBonusBalance] = useState(0);
    const [useBonus, setUseBonus] = useState(true);

    useEffect(() => {
        if (!user) return;
        api.get("/wallet/me").then((r) => {
            const bb = Number(r.data?.wallet?.bonus_balance || 0);
            setBonusBalance(bb);
        }).catch(() => {});
    }, [user]);

    const onCta = async (planId) => {
        if (!user) {
            nav(`/login?next=/pricing&plan=${planId}`);
            return;
        }
        setPaying(planId);
        const res = await startSubscriptionCheckout(planId, { useBonusBalance: useBonus && bonusBalance > 0 });
        setPaying(null);
        if (res.ok) {
            nav("/dashboard");
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAF7] relative overflow-hidden">
            <Seo
                title="Pricing — Starter, Silver, Gold & Platinum Plans"
                description="Affordable plans for every real estate professional in India. Starter ₹1,000/mo, Silver ₹2,500/3mo, Gold ₹4,500/6mo, Platinum ₹6,500/yr. 15-day free Premium trial on signup."
                path="/pricing"
            />
            {/* Ambient background */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                    background:
                        "radial-gradient(ellipse 1200px 600px at 50% -10%, rgba(218,165,32,0.16), transparent 60%), radial-gradient(ellipse 900px 500px at 100% 100%, rgba(0,38,87,0.06), transparent 60%)",
                }}
            />

            {/* HEADER */}
            <section className="relative pt-20 pb-12 lg:pt-28 lg:pb-16">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gold-500/30 mb-6 shadow-sm" data-testid="pricing-badge">
                        <Sparkles className="w-3.5 h-3.5 text-gold-600" />
                        <span className="text-xs font-semibold tracking-[0.18em] uppercase text-gold-700">Pricing · One platform, every need</span>
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-navy-500 leading-[1.05]">
                        Affordable Plans For Every<br />
                        <span className="gold-shimmer">Real Estate Professional</span>
                    </h1>
                    <p className="text-base lg:text-lg text-navy-500/70 mt-6 max-w-2xl mx-auto leading-relaxed">
                        Start small, grow big with India's most affordable property ecosystem — for <strong className="text-navy-500">brokers</strong>, <strong className="text-navy-500">advocates</strong> and <strong className="text-navy-500">customers</strong>.
                    </p>
                </div>
            </section>

            {/* PLANS */}
            <section className="relative pb-16">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    {user && bonusBalance > 0 && (
                        <div
                            data-testid="bonus-balance-banner"
                            className="mb-8 rounded-2xl border border-gold-500/40 bg-gradient-to-r from-[#FFFBEF] via-white to-[#FFF6E0] p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-sm"
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gold-500 text-white flex items-center justify-center font-bold">★</div>
                                <div>
                                    <div className="font-heading text-lg font-bold text-navy-500">You have ₹{bonusBalance.toLocaleString("en-IN")} in bonus credits</div>
                                    <div className="text-sm text-navy-500/70 mt-0.5">Non-withdrawable referral credits — apply them on any paid plan below.</div>
                                </div>
                            </div>
                            <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gold-500/40 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={useBonus}
                                    onChange={(e) => setUseBonus(e.target.checked)}
                                    className="accent-gold-500 w-4 h-4"
                                    data-testid="use-bonus-toggle"
                                />
                                <span className="text-sm font-semibold text-navy-500">Apply bonus on checkout</span>
                            </label>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 stagger items-stretch" data-testid="pricing-grid">
                        {PLANS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <div
                                    key={p.id}
                                    data-testid={`plan-${p.id}`}
                                    className={`relative rounded-3xl p-7 lg:p-8 flex flex-col transition-all duration-300 will-change-transform group ${
                                        p.accent
                                            ? "bg-gradient-to-b from-[#FFFBEF] via-white to-white border-2 border-gold-500 shadow-[0_30px_70px_-20px_rgba(218,165,32,0.45)] lg:scale-[1.03]"
                                            : "bg-white border border-navy-500/10 shadow-sm hover:border-gold-500/40 hover:-translate-y-1 hover:shadow-[0_22px_50px_-20px_rgba(0,38,87,0.18)]"
                                    }`}
                                >
                                    {p.tag && (
                                        <div className="text-on-image absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gold-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-md inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`tag-${p.id}`}>
                                            <Star className="w-3 h-3 fill-white" /> {p.tag}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between mb-5">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                                            p.accent ? "bg-gold-500 text-on-image shadow-md" : "bg-gold-500/10 text-gold-600 group-hover:bg-gold-500 group-hover:text-on-image"
                                        }`}>
                                            <Icon className={`w-6 h-6 ${p.accent ? "text-white" : "group-hover:text-white"}`} />
                                        </div>
                                        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${
                                            p.accent ? "bg-gold-500/15 text-gold-700" : "bg-navy-500/8 text-navy-500/70"
                                        }`}>{p.tier}</span>
                                    </div>

                                    <h3 className="font-heading text-2xl font-bold text-navy-500">{p.name}</h3>
                                    <p className="text-sm text-navy-500/65 mt-1.5 leading-relaxed min-h-[60px]">{p.description}</p>

                                    {/* Audience pills */}
                                    <div className="flex flex-wrap gap-1.5 mt-3 mb-1" data-testid={`audience-${p.id}`}>
                                        {p.audience.map((a) => {
                                            const A = AUDIENCE_ICON[a] || User;
                                            return (
                                                <span key={a} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                                    p.accent ? "bg-gold-500/10 text-gold-700 border border-gold-500/30" : "bg-navy-500/6 text-navy-500/75 border border-navy-500/10"
                                                }`}>
                                                    <A className="w-3 h-3" /> {a}
                                                </span>
                                            );
                                        })}
                                    </div>

                                    <div className="mt-5 mb-6 flex items-baseline gap-2 flex-wrap">
                                        <span className={`font-heading text-4xl lg:text-5xl font-bold leading-none ${p.accent ? "gold-shimmer" : "text-navy-500"}`}>{p.price}</span>
                                        <span className="text-xs text-navy-500/60 font-medium">{p.priceSuffix}</span>
                                    </div>

                                    <ul className="space-y-3 mb-8" data-testid={`features-${p.id}`}>
                                        {p.features.map((f) => (
                                            <li key={f} className="text-sm text-navy-500/85 flex items-start gap-2.5 leading-snug">
                                                <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                                                    p.accent ? "bg-gold-500 text-on-image" : "bg-gold-500/10 text-gold-600"
                                                }`}>
                                                    <Check className={`w-3 h-3 stroke-[3] ${p.accent ? "text-white" : ""}`} />
                                                </span>
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        type="button"
                                        onClick={() => onCta(p.id)}
                                        disabled={paying !== null}
                                        data-testid={`cta-${p.id}`}
                                        className={`text-on-image mt-auto block w-full text-center px-5 py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed ${
                                            p.accent
                                                ? "bg-gold-500 text-white hover:bg-gold-600 shadow-md hover:shadow-[0_14px_40px_-10px_rgba(218,165,32,0.55)]"
                                                : "bg-navy-500 text-white hover:bg-[#1a3a6e] shadow-sm hover:shadow-md"
                                        }`}
                                    >
                                        {paying === p.id ? "Opening Razorpay…" : p.cta}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Premium note */}
                    <div className="mt-10 flex justify-center">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gold-500/25 shadow-sm" data-testid="trial-note">
                            <Sparkles className="w-3.5 h-3.5 text-gold-600" />
                            <span className="text-xs sm:text-sm text-navy-500/80 font-medium">
                                Every new signup gets <strong>15 days free Premium access</strong> · Subscription required after trial expires.
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* PROPERTY LEAD BOOST SERVICES */}
            <BoostServicesSection />

            {/* AUDIENCE COMPARISON STRIP */}
            <section className="relative py-12 bg-white/60 backdrop-blur-sm border-y border-navy-500/8">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <div className="label-overline mb-2">Built for everyone</div>
                        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy-500">One ecosystem, three audiences</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <AudienceCard icon={User} title="Buyers & Tenants" body="Search verified listings, save favourites, contact owners directly, and bid on auction properties." />
                        <AudienceCard icon={Building2} title="Brokers & Developers" body="List properties, capture verified leads, get featured in search and on the homepage." accent />
                        <AudienceCard icon={Scale} title="Advocates" body="Showcase your legal services, accept consultation requests, and grow a verified RERA-linked clientele." />
                    </div>
                </div>
            </section>

            {/* TRUST BADGES */}
            <section className="relative py-14">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="trust-badges">
                        {TRUST_BADGES.map((b) => {
                            const Icon = b.icon;
                            return (
                                <div key={b.label} className="flex items-center justify-center gap-3 px-4 py-4 rounded-2xl bg-white border border-navy-500/8 hover:border-gold-500/40 transition-colors" data-testid={`trust-${b.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-gold-600" />
                                    </div>
                                    <div className="text-sm font-semibold text-navy-500">{b.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ teaser / closing */}
            <section className="relative py-16 lg:py-20">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-500">
                        Need a custom plan for your <span className="gold-shimmer">enterprise team?</span>
                    </h2>
                    <p className="text-navy-500/70 mt-4 max-w-2xl mx-auto">
                        Developers, large brokerages, advocate firms and PSU/bank panels — we offer custom packages with bulk listings, white-label dashboards and on-site training.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="pricing-contact-sales"
                        className="text-on-image inline-flex items-center gap-2 mt-7 px-7 py-3.5 rounded-xl bg-navy-500 text-white font-semibold text-sm hover:bg-[#1a3a6e] transition-colors shadow-md"
                    >
                        Talk to Sales →
                    </Link>
                </div>
            </section>
        </div>
    );
}

function AudienceCard({ icon: Icon, title, body, accent }) {
    return (
        <div className={`rounded-2xl p-6 transition-all ${
            accent
                ? "bg-gradient-to-b from-[#FFFBEF] to-white border-2 border-gold-500/50 shadow-sm"
                : "bg-white border border-navy-500/10 hover:border-gold-500/40 hover:shadow-md"
        }`} data-testid={`audience-card-${title.toLowerCase().split(/[ &]+/)[0]}`}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${
                accent ? "bg-gold-500 text-on-image" : "bg-gold-500/10 text-gold-600"
            }`}>
                <Icon className={`w-5 h-5 ${accent ? "text-white" : ""}`} />
            </div>
            <h3 className="font-heading text-lg font-bold text-navy-500">{title}</h3>
            <p className="text-sm text-navy-500/70 mt-1.5 leading-relaxed">{body}</p>
        </div>
    );
}



/* ───────── PROPERTY LEAD BOOST SERVICES SECTION ───────── */
const BOOST_PLANS = [
    {
        id: "basic-boost",
        name: "Basic Boost",
        price: "₹999",
        priceValue: 999,
        duration: "3 Days Promotion",
        icon: Rocket,
        accent: false,
        tag: null,
        features: [
            "Facebook Promotion",
            "WhatsApp Marketing",
            "Featured Property Tag",
            "Basic Buyer Inquiry Support",
            "Local Audience Reach",
        ],
        cta: "Boost Now",
    },
    {
        id: "premium-lead",
        name: "Premium Lead Campaign",
        price: "₹2,499",
        priceValue: 2499,
        duration: "7 Days Promotion",
        icon: Target,
        accent: true,
        tag: "MOST POPULAR",
        features: [
            "Facebook & Instagram Meta Ads",
            "Real Buyer Lead Generation",
            "Reel & Video Promotion",
            "Targeted Audience Reach",
            "Featured Property Placement",
            "WhatsApp Lead Support",
        ],
        cta: "Get Leads",
    },
    {
        id: "advance-boost",
        name: "Advance Property Boost",
        price: "₹4,999",
        priceValue: 4999,
        duration: "15 Days Promotion",
        icon: Video,
        accent: false,
        tag: null,
        features: [
            "High Reach Meta Ads Campaign",
            "Premium Property Placement",
            "Video / Reel Marketing",
            "Buyer & Investor Targeting",
            "Daily Promotion",
            "Priority Lead Support",
        ],
        cta: "Promote Now",
    },
    {
        id: "ultimate-lead",
        name: "Ultimate Lead Campaign",
        price: "₹9,999",
        priceValue: 9999,
        duration: "30 Days Promotion",
        icon: Crown,
        accent: false,
        tag: "ENTERPRISE",
        features: [
            "Complete Property Marketing",
            "Premium Homepage Placement",
            "Daily Social Media Promotion",
            "Video / Reel Ads Campaign",
            "Dedicated Lead Support",
            "Targeted Buyer Campaign",
            "Builder & Commercial Project Promotion",
            "Maximum Visibility Across Platforms",
        ],
        cta: "Start Campaign",
    },
];

function BoostServicesSection() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const onBoostClick = (plan) => {
        if (!user) {
            nav(`/login?next=/pricing`);
            return;
        }
        setSelectedPlan({
            id: plan.id,
            name: plan.name,
            price: plan.priceValue,
            duration: plan.duration,
        });
        setPickerOpen(true);
    };

    return (
        <section className="relative py-20 bg-gradient-to-b from-white via-[#FAFAF5] to-white" data-testid="boost-services-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/40 mb-4">
                        <Megaphone className="w-3.5 h-3.5 text-gold-600" />
                        <span className="text-xs font-bold tracking-[0.18em] uppercase text-gold-700">Get Real Buyer Leads</span>
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-500 tracking-tight">
                        Property Lead Boost Services
                    </h2>
                    <p className="text-base text-navy-500/65 mt-4 max-w-2xl mx-auto leading-relaxed">
                        Skip the wait — promote your listings on Facebook, Instagram and WhatsApp and connect with real buyers in days, not months.
                    </p>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 items-stretch" data-testid="boost-grid">
                    {BOOST_PLANS.map((p) => {
                        const Icon = p.icon;
                        return (
                            <div
                                key={p.id}
                                data-testid={`boost-card-${p.id}`}
                                className={`group relative flex flex-col h-full rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 ${
                                    p.accent
                                        ? "bg-gradient-to-b from-[#FFFBEF] to-white border-2 border-gold-500/60 shadow-xl shadow-gold-500/15"
                                        : "bg-white border border-navy-500/10 hover:border-gold-500/40 shadow-sm hover:shadow-lg"
                                }`}
                            >
                                {p.tag && (
                                    <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase shadow-md ${
                                        p.accent
                                            ? "bg-gold-500 text-white"
                                            : "bg-navy-500 text-white"
                                    }`}>
                                        {p.tag}
                                    </div>
                                )}

                                {/* Icon */}
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                                    p.accent ? "bg-gold-500 text-white" : "bg-gold-500/10 text-gold-600"
                                }`}>
                                    <Icon className="w-6 h-6" />
                                </div>

                                {/* Name */}
                                <h3 className="font-heading text-xl font-bold text-navy-500">{p.name}</h3>
                                <div className="text-xs uppercase tracking-wider text-navy-500/55 mt-1">{p.duration}</div>

                                {/* Price */}
                                <div className="mt-4 mb-5">
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="font-heading text-4xl font-bold text-navy-500">{p.price}</span>
                                    </div>
                                    <div className="text-[11px] text-navy-500/55 mt-0.5">one-time · GST extra</div>
                                </div>

                                {/* Features */}
                                <ul className="space-y-2.5 text-sm text-navy-500/85 mb-6 flex-1">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2.5">
                                            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.accent ? "text-gold-600" : "text-navy-500/60"}`} />
                                            <span className="leading-snug">{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA */}
                                <button
                                    type="button"
                                    onClick={() => onBoostClick(p)}
                                    data-testid={`boost-cta-${p.id}`}
                                    className={`mt-auto w-full px-5 py-3 rounded-xl font-bold text-sm tracking-wide transition-all duration-200 ${
                                        p.accent
                                            ? "bg-gold-500 text-white hover:bg-gold-600 shadow-md hover:shadow-[0_14px_40px_-10px_rgba(218,165,32,0.55)]"
                                            : "bg-navy-500 text-white hover:bg-[#1a3a6e] shadow-sm hover:shadow-md"
                                    }`}
                                >
                                    {p.cta}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* CTA Strip */}
                <div className="mt-16 relative rounded-3xl overflow-hidden" data-testid="boost-cta-strip">
                    <div className="absolute inset-0 bg-gradient-to-br from-navy-500 via-[#1a3a6e] to-navy-700" />
                    <div className="absolute inset-0 opacity-25 grid-pattern" />
                    <div className="relative px-6 sm:px-10 py-12 sm:py-14 flex flex-col lg:flex-row items-center justify-between gap-8">
                        <div className="lg:max-w-2xl text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/20 border border-gold-500/40 mb-3">
                                <TrendingUp className="w-3.5 h-3.5 text-gold-300" />
                                <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-gold-300">Faster Closures</span>
                            </div>
                            <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                                Property Listed But No Leads?
                            </h3>
                            <p className="text-white/75 mt-3 text-base leading-relaxed">
                                Boost your property with GPM Ads and reach real buyers faster with targeted marketing campaigns.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                            <button
                                type="button"
                                onClick={() => onBoostClick(BOOST_PLANS.find((x) => x.id === "premium-lead"))}
                                data-testid="boost-cta-promote"
                                className="px-6 py-3.5 rounded-xl bg-gold-500 text-navy-900 font-bold text-sm tracking-wide hover:bg-gold-100 transition-colors shadow-lg shadow-gold-500/25 whitespace-nowrap"
                            >
                                Boost This Property →
                            </button>
                            <Link
                                to="/contact"
                                data-testid="boost-cta-expert"
                                className="px-6 py-3.5 rounded-xl border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap"
                            >
                                <MessageCircle className="w-4 h-4" /> Talk To Expert
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <BoostPropertyPicker
                open={pickerOpen}
                plan={selectedPlan}
                onClose={() => setPickerOpen(false)}
                onSuccess={() => {
                    setPickerOpen(false);
                    nav("/dashboard");
                }}
            />
        </section>
    );
}
